import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import {
  BarChart3,
  TrendingUp,
  PieChart,
  Download,
  ChevronDown,
  RefreshCw,
  Activity,
  Database,
  Wifi,
  WifiOff,
  AlertTriangle,
  FileImage,
  FileText,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import HTTPService from "../../../../utils/axios.js";

const customToast = {
  error: (message, options = {}) => {
    console.error(message);
    // For now, just log. You can integrate react-toastify if needed
  },
};

const CHART_TYPES = [
  { value: "bar", label: "Bar Chart", icon: BarChart3 },
  { value: "line", label: "Line Chart", icon: TrendingUp },
  { value: "pie", label: "Pie Chart", icon: PieChart },
  { value: "donut", label: "Donut Chart", icon: PieChart },
  { value: "radar", label: "Radar Chart", icon: Activity },
];

const ProgramsChart = () => {
  const [programStats, setProgramStats] = useState([]);
  const [enrolledProgramCount, setEnrolledProgramCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState("bar");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [apiConfigured, setApiConfigured] = useState(false);
  const [showChartDropdown, setShowChartDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [chartKey, setChartKey] = useState(0); // Force chart re-render
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const { invokeApi } = HTTPService();

  // Professional color palette with gradients
  const colors = [
    "#6366f1", // Indigo
    "#8b5cf6", // Violet
    "#06b6d4", // Cyan
    "#10b981", // Emerald
    "#f59e0b", // Amber
    "#ef4444", // Red
    "#ec4899", // Pink
    "#84cc16", // Lime
    "#f97316", // Orange
    "#3b82f6", // Blue
    "#14b8a6", // Teal
    "#a855f7", // Purple
  ];

  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        const instance = chartRef.current.getEchartsInstance();
        instance.resize();
      }
    };

    // Call it once right after mount
    handleResize();

    // Optional: also adjust on window resize
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Enhanced gradient colors for better visual appeal
  const gradientColors = [
    {
      type: "linear",
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "#6366f1" },
        { offset: 1, color: "#4f46e5" },
      ],
    },
    {
      type: "linear",
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "#8b5cf6" },
        { offset: 1, color: "#7c3aed" },
      ],
    },
    {
      type: "linear",
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "#06b6d4" },
        { offset: 1, color: "#0891b2" },
      ],
    },
    {
      type: "linear",
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "#10b981" },
        { offset: 1, color: "#059669" },
      ],
    },
    {
      type: "linear",
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "#f59e0b" },
        { offset: 1, color: "#d97706" },
      ],
    },
    {
      type: "linear",
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "#ef4444" },
        { offset: 1, color: "#dc2626" },
      ],
    },
  ];

  //   const chart = echarts.init(document.getElementById("chart"), "myTheme", {
  //     devicePixelRatio: window.devicePixelRatio,
  //   });

  const fetchProgramStats = () => {
    invokeApi({
      method: "GET",
      url: `widget/programs-submission-stats/`,
    })
      .then((data) => {
        // Handle different response structures
        let statsData = data;
        if (data.program_submission_stats) {
          statsData = data.program_submission_stats;
        } else if (data.data) {
          statsData = data.data;
        } else if (data.results) {
          statsData = data.results;
        }

        setProgramStats(Array.isArray(statsData) ? statsData : [statsData]);
        const enrolledProgCount = Array.isArray(statsData)
          ? statsData.length
          : 1;
        setEnrolledProgramCount(enrolledProgCount);
        setLastUpdated(new Date());
        setLoading(false);
      })
      .catch((error) => {
        console.error("API Error:", error);
        setError(error.message || "Something went wrong!");

        // Fallback to demo data
        setProgramStats(DEMO_DATA.program_submission_stats);
        setEnrolledProgramCount(DEMO_DATA.program_submission_stats.length);
        setLoading(false);

        customToast.error("Something went wrong! Using demo data.", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  useEffect(() => {
    fetchProgramStats();
  }, []);

  // Prepare data for charts
  const chartData = programStats.map((program, index) => ({
    name: program.program_title || `Program ${index + 1}`,
    value: program.total_program_submissions || 0,
    percentage: program.percentage_of_total_submissions || 0,
    programId: program.program_id || `PROG${index + 1}`,
    totalSubmissions: program.total_submissions || 0,
  }));

  const hasSubmissions = chartData.some((item) => item.value > 0);

  // Handle chart type change with force re-render
  const handleChartTypeChange = useCallback((newType) => {
    setSelectedChartType(newType);
    setChartKey((prev) => prev + 1); // Force chart re-render
    setShowChartDropdown(false);
  }, []);

  // Download chart as JPG
  const downloadAsJPG = useCallback(() => {
    setShowDownloadDropdown(false);

    setTimeout(() => {
      if (chartRef.current) {
        try {
          const echartInstance = chartRef.current.getEchartsInstance();
          const url = echartInstance.getDataURL({
            type: "jpeg",
            pixelRatio: 3,
            backgroundColor: "#fff",
          });

          const link = document.createElement("a");
          link.href = url;
          link.download = `${selectedChartType}-chart-${new Date()
            .toISOString()
            .slice(0, 10)}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error("Error downloading JPG:", error);
          alert("Error downloading chart. Please try again.");
        }
      } else {
        console.error("Chart reference not available");
        alert("Chart not ready. Please try again.");
      }
    }, 100);
  }, [selectedChartType]);

  // Download chart as PDF
  const downloadAsPDF = useCallback(() => {
    setShowDownloadDropdown(false);

    setTimeout(async () => {
      if (chartContainerRef.current) {
        try {
          const canvas = await html2canvas(chartContainerRef.current, {
            backgroundColor: "#ffffff",
            scale: 3,
            useCORS: true,
            allowTaint: true,
          });

          const imgData = canvas.toDataURL("image/jpeg", 1.0);
          const pdf = new jsPDF("landscape", "mm", "a4");

          const imgWidth = 280;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          pdf.addImage(imgData, "JPEG", 10, 10, imgWidth, imgHeight);
          pdf.save(
            `${selectedChartType}-chart-${new Date()
              .toISOString()
              .slice(0, 10)}.pdf`
          );
        } catch (error) {
          console.error("Error generating PDF:", error);
          // Fallback to JPG download
          downloadAsJPG();
        }
      } else {
        console.error("Chart container reference not available");
        alert("Chart not ready. Please try again.");
      }
    }, 100);
  }, [selectedChartType, downloadAsJPG]);

  // Chart configurations with enhanced colors
  const getChartOption = useCallback(() => {
    const baseTooltip = {
      formatter: (params) => {
        if (Array.isArray(params)) {
          const data = params[0];
          return `<strong>${data.name}</strong><br/>
                  Submissions: ${data.value}<br/>
                  Percentage: ${chartData[data.dataIndex]?.percentage.toFixed(
                    2
                  )}%`;
        } else {
          return `<strong>${params.name}</strong><br/>
                  Submissions: ${params.value}<br/>
                  Percentage: ${params.percent}%`;
        }
      },
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      borderColor: "#6366f1",
      borderWidth: 1,
      textStyle: {
        color: "#fff",
        fontSize: 12,
      },
    };

    switch (selectedChartType) {
      case "bar":
        return {
          title: {
            text: "Program Submissions - Bar Chart",
            left: "center",
            textStyle: {
              fontSize: 18,
              fontWeight: "600",
              color: "#1f2937", // Tailwind slate-800
              fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
            },
          },

          tooltip: {
            ...baseTooltip,
            trigger: "axis",
            axisPointer: { type: "shadow" },
          },
          grid: { left: "4%", right: "10%", bottom: "10%", containLabel: true },
          xAxis: {
            type: "category",
            data: chartData.map((item) =>
              item.name.length > 20
                ? item.name.substring(0, 20) + "..."
                : item.name
            ),
            name: "Programs",
            nameLocation: "end", // 👈 This aligns it to the right
            nameGap: 0, // Adjust spacing from axis
            nameTextStyle: { color: "rgb(43, 44, 44)", fontSize: 12 },
            axisLabel: {
              rotate: 45, // Keep if needed for long labels
              fontSize: 12,
              color: "#6b7280",
            },
            axisLine: { lineStyle: { color: "#e5e7eb" } },
          },

          yAxis: {
            type: "value",
            name: "Submissions",
            nameTextStyle: { color: "rgb(43, 44, 44)", fontSize: 12 },
            axisLabel: { color: "#6b7280" },
            axisLine: { lineStyle: { color: "#e5e7eb" } },
            splitLine: { lineStyle: { color: "#f3f4f6" } },
          },
          series: [
            {
              type: "bar",
              data: chartData.map((item, index) => ({
                value: item.value,
                itemStyle: {
                  color: gradientColors[index % gradientColors.length],
                },
              })),
              barWidth: "60%",
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: "rgba(0, 0, 0, 0.3)",
                },
              },
            },
          ],
        };

      case "line":
        return {
          title: {
            text: "Program Submissions - Line Chart",
            left: "center",
            textStyle: {
              fontSize: 18,
              fontWeight: "bold",
              color: "#1f2937", // Tailwind slate-800
              fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
            },
          },
          tooltip: { ...baseTooltip, trigger: "axis" },
          grid: { left: "4%", right: "10%", bottom: "10%", containLabel: true },
          xAxis: {
            type: "category",
            data: chartData.map((item, index) => `P${index + 1}`),
            name: "Priority",
            nameLocation: "end", // 👈 This aligns it to the right
            nameGap: 0, // Adjust spacing from axis
            nameTextStyle: { color: "rgb(43, 44, 44)", fontSize: 12 },
            axisLabel: {
              rotate: 45, // Keep if needed for long labels
              fontSize: 11,
              color: "#6b7280",
            },
            axisLine: { lineStyle: { color: "#e5e7eb" } },
          },

          yAxis: {
            type: "value",
            name: "Submissions",
            nameTextStyle: { color: "rgb(43, 44, 44)", fontSize: 12 },
            axisLabel: { color: "#6b7280" },
            axisLine: { lineStyle: { color: "#e5e7eb" } },
            splitLine: { lineStyle: { color: "#f3f4f6" } },
          },
          series: [
            {
              type: "line",
              data: chartData.map((item) => item.value),
              smooth: true,
              lineStyle: { width: 4, color: "#6366f1" },
              itemStyle: {
                color: "#6366f1",
                borderWidth: 3,
                borderColor: "#fff",
                shadowBlur: 10,
                shadowColor: "rgba(99, 102, 241, 0.3)",
              },
              areaStyle: {
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: "rgba(99, 102, 241, 0.3)" },
                    { offset: 1, color: "rgba(99, 102, 241, 0.05)" },
                  ],
                },
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 15,
                  shadowColor: "rgba(99, 102, 241, 0.5)",
                },
              },
            },
          ],
        };

      case "pie":
        return {
          title: {
            text: "Program Distribution - Pie Chart",
            left: "center",
            textStyle: { fontSize: 18, fontWeight: "bold", color: "#374151" },
          },
          tooltip: {
            trigger: "item",
            formatter: "{b}: {c} ({d}%)",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            borderColor: "#6366f1",
            borderWidth: 1,
            textStyle: { color: "#fff", fontSize: 12 },
          },
          legend: {
            bottom: "15",
            left: "center",
            textStyle: { fontSize: 11, color: "#6b7280" },
          },
          series: [
            {
              name: "Programs",
              type: "pie",
              radius: ["0%", "70%"],
              center: ["50%", "50%"],
              data: chartData.map((item, index) => {
                const percentage = item.percentage || 0;

                return {
                  value: item.value,
                  name:
                    item.name.length > 15
                      ? item.name.substring(0, 15) + "..."
                      : item.name,
                  itemStyle: {
                    color: colors[index % colors.length],
                    borderColor: "#fff",
                    borderWidth: 2,
                    shadowBlur: 10,
                    shadowColor: "rgba(0, 0, 0, 0.1)",
                  },
                  label: {
                    show: true,
                    position: "inside",
                    formatter: "{d}%",
                    rotate: percentage < 20 ? 90 : "tangential", // ✅ Condition here
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: "bold",
                  },
                };
              }),
              label: {
                show: true,
                position: "inside",
                formatter: "{d}%",
                color: "#fff",
                fontSize: 15,
                fontWeight: "bold",
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 15,
                  shadowOffsetX: 0,
                  shadowColor: "rgba(0, 0, 0, 0.3)",
                },
              },
            },
          ],
        };

      case "donut":
        return {
          title: {
            text: "Program Distribution - Donut Chart",
            left: "center",
            textStyle: { fontSize: 18, fontWeight: "bold", color: "#374151" },
          },
          tooltip: {
            trigger: "item",
            formatter: "{b}: {c} ({d}%)",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            borderColor: "#6366f1",
            borderWidth: 1,
            textStyle: { color: "#fff", fontSize: 12 },
          },
          legend: {
            bottom: "15",
            left: "center",
            orient: "horizontal",
            itemGap: 10,
            textStyle: {
              fontSize: 12,
              color: "#4B5563",
            },
            formatter: function (name) {
              return name.length > 20 ? name.substring(0, 20) + "…" : name;
            },
          },
          series: [
            {
              name: "Programs",
              type: "pie",
              radius: ["40%", "70%"],
              center: ["50%", "50%"],
              data: chartData.map((item, index) => {
                const percentage = item.percentage || 0;

                return {
                  value: item.value,
                  name:
                    item.name.length > 15
                      ? item.name.substring(0, 15) + "..."
                      : item.name,
                  itemStyle: {
                    color: colors[index % colors.length],
                    borderColor: "#fff",
                    borderWidth: 2,
                    shadowBlur: 10,
                    shadowColor: "rgba(0, 0, 0, 0.1)",
                  },
                  label: {
                    show: true,
                    position: "inside",
                    formatter: "{d}%",
                    rotate: percentage < 20 ? 90 : "tangential", // ✅ Condition here
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: "bold",
                  },
                };
              }),
              label: {
                show: true,
                position: "inside",
                formatter: "{d}%",
                color: "#fff",
                fontSize: 15,
                fontWeight: "bold",
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 15,
                  shadowOffsetX: 0,
                  shadowColor: "rgba(0, 0, 0, 0.3)",
                },
              },
            },
          ],
        };

      case "radar":
        return {
          title: {
            text: "Program Performance - Radar Chart",
            left: "center",
            textStyle: { fontSize: 18, fontWeight: "bold", color: "#374151" },
          },
          tooltip: {
            trigger: "item",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            borderColor: "#6366f1",
            borderWidth: 1,
            textStyle: { color: "#fff", fontSize: 14 },
          },
          radar: {
            indicator: chartData.slice(0, 8).map((item) => ({
              name:
                item.name.length > 12
                  ? item.name.substring(0, 12) + "..."
                  : item.name,
              max: Math.max(...chartData.map((d) => d.value)) * 1.2,
            })),
            center: ["50%", "60%"],
            radius: "60%",
            axisLine: { lineStyle: { color: "#e5e7eb" } },
            splitLine: { lineStyle: { color: "#f3f4f6" } },
            name: {
              textStyle: { color: " rgb(26, 27, 27)", fontSize: 14 },
            },
          },
          series: [
            {
              name: "Program Submissions",
              type: "radar",
              data: [
                {
                  value: chartData.slice(0, 8).map((item) => item.value),
                  name: "Submissions",
                  itemStyle: { color: "#6366f1" },
                  areaStyle: {
                    color: "rgba(99, 102, 241, 0.2)",
                    shadowBlur: 10,
                    shadowColor: "rgba(99, 102, 241, 0.3)",
                  },
                  lineStyle: {
                    width: 3,
                    color: "#6366f1",
                  },
                },
              ],
              emphasis: {
                areaStyle: {
                  color: "rgba(99, 102, 241, 0.3)",
                },
              },
            },
          ],
        };

      default:
        return {};
    }
  }, [selectedChartType, chartData]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showChartDropdown || showDownloadDropdown) {
        setShowChartDropdown(false);
        setShowDownloadDropdown(false);
      }
    };

    if (showChartDropdown || showDownloadDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showChartDropdown, showDownloadDropdown]);

  const totalSubmissions = chartData.reduce((sum, item) => sum + item.value, 0);
  const avgSubmissions = totalSubmissions / (chartData.length || 1);

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div
              className="card shadow-sm border-0"
              style={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div className="card-body text-center py-5">
                <div
                  className="spinner-border text-primary mb-3"
                  style={{ width: "3rem", height: "3rem" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-muted">Loading Programs Data...</h5>
                <p className="text-muted mb-0">
                  Preparing charts and analytics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header with stats and controls */}
      {/* <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <div className="mb-2 mb-md-0">
              <h3 className="text-white mb-1 d-flex align-items-center">
                <Activity size={24} className="me-2" />
                Programs Submission Analytics
              </h3>
              <p className="text-white-50 mb-0">
                Interactive charts with download capability
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {apiConfigured ? (
                <Wifi size={20} className="text-success" />
              ) : (
                <WifiOff size={20} className="text-warning" />
              )}
              <button
                className="btn btn-light btn-sm"
                onClick={fetchProgramStats}
                disabled={loading}
              >
                <RefreshCw
                  size={16}
                  className={loading ? "spin me-2" : "me-2"}
                />
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          // Stats Cards
          <div className="row mb-4">
            <div className="col-6 col-md-3">
              <div
                className="card bg-primary text-white h-100"
                style={{ borderRadius: "15px" }}
              >
                <div className="card-body text-center p-3">
                  <Database size={24} className="mb-2" />
                  <h6 className="mb-1">Total Programs</h6>
                  <h4 className="mb-0">{enrolledProgramCount}</h4>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div
                className="card bg-success text-white h-100"
                style={{ borderRadius: "15px" }}
              >
                <div className="card-body text-center p-3">
                  <BarChart3 size={24} className="mb-2" />
                  <h6 className="mb-1">Total Submissions</h6>
                  <h4 className="mb-0">{totalSubmissions.toLocaleString()}</h4>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div
                className="card bg-warning text-white h-100"
                style={{ borderRadius: "15px" }}
              >
                <div className="card-body text-center p-3">
                  <TrendingUp size={24} className="mb-2" />
                  <h6 className="mb-1">Average/Program</h6>
                  <h4 className="mb-0">{avgSubmissions.toFixed(0)}</h4>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div
                className="card bg-info text-white h-100"
                style={{ borderRadius: "15px" }}
              >
                <div className="card-body text-center p-3">
                  <Activity size={24} className="mb-2" />
                  <h6 className="mb-1">Top Program</h6>
                  <h4 className="mb-0">
                    {chartData.length > 0
                      ? Math.max(...chartData.map((d) => d.value))
                      : 0}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          Alert Messages
          {!apiConfigured && (
            <div
              className="alert alert-warning d-flex align-items-center mb-4"
              role="alert"
            >
              <WifiOff size={20} className="me-2" />
              <div>
                <strong>Demo Mode:</strong> Configure your API settings in{" "}
                <code>src/config/api.js</code> to use live data.
              </div>
            </div>
          )}

          {error && programStats.length > 0 && (
            <div
              className="alert alert-warning d-flex align-items-center mb-4"
              role="alert"
            >
              <AlertTriangle size={20} className="me-2" />
              <div className="flex-grow-1">
                <strong>API Connection Issue:</strong> {error}
                <br />
                <small>
                  Showing {apiConfigured ? "cached" : "demo"} data below.
                </small>
              </div>
              <button
                className="btn btn-outline-warning btn-sm ms-2"
                onClick={fetchProgramStats}
                disabled={loading}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div> */}

      {/* Chart Section */}
      {chartData.length > 0 && hasSubmissions ? (
        <div className="row">
          <div className="col-12">
            <div
              className="border-0"
              style={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div className="d-flex justify-content-between align-items-center mt-3 w-100">
                  {/* Chart Type Dropdown */}
                  <div className="position-relative">
                    <button
                      className="btn btn-sm d-flex align-items-center ms-3"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowChartDropdown(!showChartDropdown);
                        setShowDownloadDropdown(false);
                      }}
                    >
                      <BarChart3 size={16} className="me-1" />
                      Chart Type
                      <ChevronDown size={14} className="ms-1" />
                    </button>
                    {showChartDropdown && (
                      <div
                        className="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg"
                        style={{ minWidth: "200px", zIndex: 1000 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {CHART_TYPES.map((type) => (
                          <button
                            key={type.value}
                            className={`d-flex align-items-center w-100 px-3 py-2 border-0 bg-transparent text-start ${
                              selectedChartType === type.value
                                ? "bg-light text-primary"
                                : "text-dark"
                            }`}
                            style={{ fontSize: "14px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleChartTypeChange(type.value);
                            }}
                          >
                            <type.icon size={16} className="me-2" />
                            {type.label}
                            {selectedChartType === type.value && (
                              <span className="ms-auto">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Download Dropdown */}
                  <div className="position-relative">
                    <button
                      className="btn btn-sm d-flex align-items-center me-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDownloadDropdown(!showDownloadDropdown);
                        setShowChartDropdown(false);
                      }}
                      title="Download Chart"
                    >
                      <Download size={16} className="me-1" />
                      Download
                      <ChevronDown size={14} className="ms-1" />
                    </button>
                    {showDownloadDropdown && (
                      <div
                        className="position-absolute top-100 end-0 mt-1 bg-white border rounded shadow-lg"
                        style={{ minWidth: "150px", zIndex: 1000 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="d-flex align-items-center w-100 px-3 py-2 border-0 bg-transparent text-start text-dark"
                          style={{ fontSize: "14px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadAsJPG();
                          }}
                        >
                          <FileImage size={16} className="me-2" />
                          Download as JPG
                        </button>
                        <button
                          className="d-flex align-items-center w-100 px-3 py-2 border-0 bg-transparent text-start text-dark"
                          style={{ fontSize: "14px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadAsPDF();
                          }}
                        >
                          <FileText size={16} className="me-2" />
                          Download as PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Chart Body */}
              <div
                className="card-body p-4"
                ref={chartContainerRef}
                style={{
                  height: "500px",
                  width: "100%",
                }}
              >
                <ReactECharts
                  ref={chartRef}
                  key={`chart-${selectedChartType}-${chartKey}`}
                  option={getChartOption()}
                  style={{
                    height: "500px",
                    width: "900px", // Or any large fixed width for high resolution
                    maxWidth: "100%",
                    display: "block",
                  }}
                  opts={{
                    renderer: "canvas",
                    width: "auto",
                    height: "auto",
                    devicePixelRatio: 2, // Force it manually
                  }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <div
              className="card shadow-sm border-0"
              style={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div className="card-body text-center py-5">
                <Database size={48} className="text-muted mb-3" />
                <h5 className="text-muted">No Submissions Data Found</h5>
                <p className="text-muted">
                  There are no submissions to display at the moment.
                </p>
                <button className="btn btn-primary" onClick={fetchProgramStats}>
                  <RefreshCw size={16} className="me-2" />
                  Refresh Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer with last updated time */}
      {lastUpdated && chartData.length > 0 && (
        <div className="row mt-4">
          <div className="col-12 text-center"></div>
        </div>
      )}
    </>
  );
};

export default ProgramsChart;
